import { useState, useCallback, useEffect, useRef } from "react";
import { 
  ArrowLeft,
  DownloadSimple,
  Warning,
  CheckCircle,
  Info,
  FileText,
  Buildings,
  MapPin,
  Calendar,
  Spinner,
  Link as LinkIcon,
  FilePdf,
  FileImage,
  Table as TableIcon,
  CaretDown,
} from "@phosphor-icons/react";
import { Button } from "../ui/button";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../../api";

export default function EvaluationReport({ sessionId, evaluation, annotations = [], activeIssueIndex, onIssueClick, onBack }) {
  const [downloading, setDownloading] = useState(null); // null | "pdf" | "annotated" | "csv"
  const [exportOpen, setExportOpen] = useState(false);

  const triggerDownload = async (url, filename, type) => {
    setDownloading(type);
    setExportOpen(false);
    try {
      const response = await axios.get(url, { responseType: "blob" });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
      toast.success("Export downloaded");
    } catch {
      toast.error("Export failed — please try again");
    } finally {
      setDownloading(null);
    }
  };

  const handleDownloadPdf = () =>
    triggerDownload(
      `${API}/download-report/${sessionId}`,
      `compliance_report_${sessionId.slice(0, 8)}.pdf`,
      "pdf"
    );

  const handleDownloadAnnotated = () =>
    triggerDownload(
      `${API}/download-report/${sessionId}/annotated`,
      `annotated_report_${sessionId.slice(0, 8)}.pdf`,
      "annotated"
    );

  const handleDownloadCsv = () =>
    triggerDownload(
      `${API}/download-report/${sessionId}/csv`,
      `issues_${sessionId.slice(0, 8)}.csv`,
      "csv"
    );

  const getSeverityClass = (text) => {
    const lower = text.toLowerCase();
    if (lower.includes("critical")) return "severity-critical";
    if (lower.includes("major")) return "severity-major";
    if (lower.includes("minor")) return "severity-minor";
    return "severity-compliant";
  };

  const getSeverityIcon = (text) => {
    const lower = text.toLowerCase();
    if (lower.includes("critical") || lower.includes("major")) {
      return <Warning size={16} weight="fill" />;
    }
    if (lower.includes("minor")) {
      return <Info size={16} weight="fill" />;
    }
    return <CheckCircle size={16} weight="fill" />;
  };

  const gatheredInfo = evaluation?.gathered_info || {};
  const overallStatus = evaluation?.overall_status || "";
  const summary = evaluation?.summary || "";
  const issues = evaluation?.issues || [];
  const compliantElements = evaluation?.compliant_elements || [];
  const recommendations = evaluation?.recommendations || [];
  const references = evaluation?.references || [];
  const rawReport = evaluation?.raw_report || "";

  const hasParsedData = issues.length > 0 || compliantElements.length > 0 || recommendations.length > 0;

  // Map issue_index → {annotationNumber (1-based), annotation obj}
  const annotationByIssue = new Map(
    annotations.map((ann, idx) => [ann.issue_index, { ...ann, num: idx + 1 }])
  );

  // Ref map to scroll active issue into view
  const issueRefs = useRef({});

  useEffect(() => {
    if (activeIssueIndex !== null && annotationByIssue.has(activeIssueIndex)) {
      const el = issueRefs.current[activeIssueIndex];
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [activeIssueIndex]);

  // Count issues by severity
  const criticalCount = issues.filter(i => i.toLowerCase().includes("critical")).length;
  const majorCount = issues.filter(i => i.toLowerCase().includes("major")).length;
  const minorCount = issues.length - criticalCount - majorCount;

  return (
    <div className="h-full flex flex-col" data-testid="evaluation-report">
      {/* Header */}
      <div className="px-4 py-3 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={onBack}
            className="h-8 px-2"
            data-testid="back-button"
          >
            <ArrowLeft size={18} />
          </Button>
          <div>
            <h2 className="font-semibold text-slate-800">Compliance Report</h2>
            <p className="text-xs text-slate-500">
              {gatheredInfo.drawing_type?.replace("_", " ").toUpperCase() || "Technical Drawing"} Evaluation
            </p>
          </div>
        </div>

        {/* Export dropdown */}
        <div className="relative" data-testid="export-dropdown-container">
          <Button
            onClick={() => setExportOpen(v => !v)}
            disabled={!!downloading}
            className="rounded-sm bg-primary hover:bg-primary-hover text-xs flex items-center gap-1"
            data-testid="export-dropdown-btn"
          >
            {downloading ? (
              <Spinner size={14} className="animate-spin mr-1" />
            ) : (
              <DownloadSimple size={14} className="mr-1" />
            )}
            Export
            <CaretDown size={12} className={`ml-0.5 transition-transform ${exportOpen ? "rotate-180" : ""}`} />
          </Button>

          {exportOpen && (
            <div className="absolute right-0 top-full mt-1 w-52 bg-white border border-slate-200 rounded shadow-lg z-50">
              <button
                onClick={handleDownloadPdf}
                className="flex items-center gap-2.5 w-full px-3 py-2.5 text-xs text-slate-700 hover:bg-slate-50 transition-colors"
                data-testid="export-pdf-btn"
              >
                <FilePdf size={15} className="text-primary flex-shrink-0" />
                <div className="text-left">
                  <div className="font-medium">PDF Report</div>
                  <div className="text-slate-400">Issues, recommendations & references</div>
                </div>
              </button>
              <button
                onClick={handleDownloadAnnotated}
                className="flex items-center gap-2.5 w-full px-3 py-2.5 text-xs text-slate-700 hover:bg-slate-50 transition-colors border-t border-slate-100"
                data-testid="export-annotated-btn"
              >
                <FileImage size={15} className="text-orange-500 flex-shrink-0" />
                <div className="text-left">
                  <div className="font-medium">PDF with Drawing</div>
                  <div className="text-slate-400">Annotated drawing + full report</div>
                </div>
              </button>
              <button
                onClick={handleDownloadCsv}
                className="flex items-center gap-2.5 w-full px-3 py-2.5 text-xs text-slate-700 hover:bg-slate-50 transition-colors border-t border-slate-100"
                data-testid="export-csv-btn"
              >
                <TableIcon size={15} className="text-green-600 flex-shrink-0" />
                <div className="text-left">
                  <div className="font-medium">Export CSV</div>
                  <div className="text-slate-400">Issues list as spreadsheet</div>
                </div>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Report Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-6">
        {/* Project Summary */}
        <div className="bg-white border border-slate-200 rounded-sm p-4">
          <h3 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
            <Buildings size={18} className="text-primary" />
            Project Summary
          </h3>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div className="flex items-center gap-2">
              <FileText size={14} className="text-slate-400" />
              <span className="text-slate-500">Type:</span>
              <span className="font-medium text-slate-800">
                {gatheredInfo.drawing_type?.replace("_", " ").replace(/\b\w/g, l => l.toUpperCase()) || "N/A"}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <MapPin size={14} className="text-slate-400" />
              <span className="text-slate-500">Location:</span>
              <span className="font-medium text-slate-800">
                {gatheredInfo.city ? `${gatheredInfo.city}, ` : ""}{gatheredInfo.state || "N/A"}
              </span>
            </div>
            <div className="flex items-center gap-2 col-span-2">
              <Calendar size={14} className="text-slate-400" />
              <span className="text-slate-500">Evaluated:</span>
              <span className="font-medium text-slate-800">
                {new Date().toLocaleDateString('en-AU', { 
                  day: 'numeric', 
                  month: 'long', 
                  year: 'numeric' 
                })}
              </span>
            </div>
          </div>
          {/* Overall status + AI summary */}
          {(overallStatus || summary) && (
            <div className={`mt-3 pt-3 border-t border-slate-100`}>
              {overallStatus && (
                <span className={`inline-block text-xs font-bold px-2 py-1 rounded mb-2 ${
                  overallStatus === "COMPLIANT"
                    ? "bg-green-100 text-green-700"
                    : "bg-red-100 text-red-700"
                }`}>
                  {overallStatus}
                </span>
              )}
              {summary && <p className="text-sm text-slate-600 leading-relaxed">{summary}</p>}
            </div>
          )}
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-red-50 border border-red-200 rounded-sm p-3 text-center">
            <div className="text-2xl font-bold text-red-600">{criticalCount}</div>
            <div className="text-xs text-red-700 font-medium">CRITICAL</div>
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded-sm p-3 text-center">
            <div className="text-2xl font-bold text-amber-600">{majorCount}</div>
            <div className="text-xs text-amber-700 font-medium">MAJOR</div>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-sm p-3 text-center">
            <div className="text-2xl font-bold text-blue-600">{minorCount}</div>
            <div className="text-xs text-blue-700 font-medium">MINOR</div>
          </div>
        </div>

        {/* Issues */}
        {issues.length > 0 && (
          <div className="bg-white border border-slate-200 rounded-sm">
            <div className="px-4 py-3 border-b border-slate-200 bg-slate-50">
              <h3 className="font-semibold text-slate-800 flex items-center gap-2">
                <Warning size={18} className="text-warning" />
                Compliance Issues ({issues.length})
              </h3>
            </div>
            <div className="divide-y divide-slate-100">
              {issues.map((issue, idx) => {
                const annInfo = annotationByIssue.get(idx);
                const isActive = activeIssueIndex === idx;
                const isClickable = !!annInfo;
                return (
                  <div
                    key={idx}
                    ref={el => issueRefs.current[idx] = el}
                    onClick={() => isClickable && onIssueClick && onIssueClick(idx)}
                    className={`p-3 transition-colors ${isActive ? 'bg-red-50 ring-1 ring-inset ring-red-200' : 'hover:bg-slate-50'} ${isClickable ? 'cursor-pointer' : ''}`}
                    data-testid={`issue-item-${idx}`}
                  >
                    <div className="flex items-start gap-3">
                      <span className={`inline-flex items-center justify-center px-2 py-1 text-xs font-medium rounded flex-shrink-0 ${getSeverityClass(issue)}`}>
                        {getSeverityIcon(issue)}
                      </span>
                      <p className="text-sm text-slate-700 flex-1">{issue}</p>
                      {annInfo && (
                        <span
                          className={`flex-shrink-0 inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold text-white ${isActive ? 'bg-red-600' : 'bg-red-400'}`}
                          title="Click to highlight on drawing"
                        >
                          {annInfo.num}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Compliant Elements */}
        {compliantElements.length > 0 && (
          <div className="bg-white border border-slate-200 rounded-sm">
            <div className="px-4 py-3 border-b border-slate-200 bg-slate-50">
              <h3 className="font-semibold text-slate-800 flex items-center gap-2">
                <CheckCircle size={18} className="text-green-600" />
                Compliant Elements ({compliantElements.length})
              </h3>
            </div>
            <div className="p-4 space-y-2">
              {compliantElements.slice(0, 8).map((element, idx) => (
                <div key={idx} className="flex items-start gap-2 text-sm">
                  <CheckCircle size={14} className="text-green-500 mt-0.5 flex-shrink-0" />
                  <span className="text-slate-600">{element}</span>
                </div>
              ))}
              {compliantElements.length > 8 && (
                <p className="text-xs text-slate-400 pt-2">
                  +{compliantElements.length - 8} more compliant elements
                </p>
              )}
            </div>
          </div>
        )}

        {/* Recommendations */}
        {recommendations.length > 0 && (
          <div className="bg-white border border-slate-200 rounded-sm">
            <div className="px-4 py-3 border-b border-slate-200 bg-slate-50">
              <h3 className="font-semibold text-slate-800 flex items-center gap-2">
                <Info size={18} className="text-primary" />
                Recommendations
              </h3>
            </div>
            <div className="p-4 space-y-3">
              {recommendations.map((rec, idx) => (
                <div key={idx} className="flex items-start gap-3 text-sm">
                  <span className="bg-primary/10 text-primary font-mono text-xs px-2 py-0.5 rounded flex-shrink-0">
                    {idx + 1}
                  </span>
                  <span className="text-slate-600">{rec}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* References */}
        {references.length > 0 && (
          <div className="bg-white border border-slate-200 rounded-sm">
            <div className="px-4 py-3 border-b border-slate-200 bg-slate-50">
              <h3 className="font-semibold text-slate-800 flex items-center gap-2">
                <LinkIcon size={18} className="text-slate-500" />
                Official References
              </h3>
            </div>
            <div className="p-4 space-y-2">
              {references.map((ref, idx) => (
                <p key={idx} className="text-sm font-mono text-primary">
                  {ref}
                </p>
              ))}
            </div>
          </div>
        )}

        {/* Raw report fallback — shown when JSON parsing yielded no structured data */}
        {!hasParsedData && rawReport && (
          <div className="bg-white border border-slate-200 rounded-sm">
            <div className="px-4 py-3 border-b border-slate-200 bg-slate-50">
              <h3 className="font-semibold text-slate-800 flex items-center gap-2">
                <FileText size={18} className="text-primary" />
                Evaluation Report
              </h3>
            </div>
            <div className="p-4">
              <pre className="text-sm text-slate-700 whitespace-pre-wrap font-sans leading-relaxed">
                {rawReport}
              </pre>
            </div>
          </div>
        )}

        {/* Disclaimer */}
        <div className="bg-slate-100 border border-slate-200 rounded-sm p-4">
          <p className="text-xs text-slate-500 leading-relaxed">
            <strong>Disclaimer:</strong> This report is generated by an AI-powered evaluation system 
            and should be used as a guide only. Final compliance determination must be made by 
            qualified professionals in consultation with relevant authorities. All standards 
            referenced should be verified against current official publications from Standards 
            Australia and the Australian Building Codes Board.
          </p>
        </div>
      </div>
    </div>
  );
}
